import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import axios from 'axios';
import { useToaster } from "../../../contexts/ToasterContext";
import dagre from 'dagre';
import { normalizeInsight, getConnectionKey} from './utils/helpers';
import { buildNodeData } from './utils/nodeBuilder';
import ShimmerLoader from './components/ShimmerLoader';
import BottomPanels from './components/BottomPanels';
import { ENV } from '../../../utils/env.js';
import { generateGitHubBlastRadius, generateS3BlastRadius } from '../../../utils/mockBlastRadius';

const ContextualNodeGraph = ({ insightId, findingId, type = 'repo' }) => {
  const { showToast } = useToaster();
  const [visibleNodes, setVisibleNodes] = useState(new Set(['github-token']));
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [insightData, setInsightData] = useState(null);
  const [collapsePanels, setCollapsePanels] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastFetchedId, setLastFetchedId] = useState(null);

  // Use either insightId (for repo) or findingId (for s3)
  const currentId = type === 's3' ? findingId : insightId;

  // Reset state when ID changes
  useEffect(() => {
    setInsightData(null);
    setError(null);
    setLastFetchedId(null);
  }, [currentId]);

  // Fetch insights data from API with mock fallback
  useEffect(() => {
    const fetchInsightsData = async () => {
      if (!currentId) {
        setError(`No ${type === 's3' ? 'finding' : 'insight'} ID provided`);
        setLoading(false);
        return;
      }

      // Prevent multiple calls for the same ID
      if (lastFetchedId === currentId) {
        return;
      }

      try {
        setLoading(true);
        setLastFetchedId(currentId);
        
        // Use mock data directly for GitHub and S3 (no API calls needed)
        if (type === 'github' || type === 'repo') {
          // Extract owner and repo from insightId or use defaults
          // insightId might be in format "owner/repo" or just "repo-id"
          let owner = 'acme-corp';
          let repoName = currentId;
          
          if (currentId && currentId.includes('/')) {
            const parts = currentId.split('/');
            owner = parts[0];
            repoName = parts[1] || currentId;
          }
          
          const mockData = generateGitHubBlastRadius(currentId, owner, repoName);
          setInsightData(mockData);
          setError(null);
          setLoading(false);
          return;
        } else if (type === 's3') {
          // Extract bucket name from findingId or use default
          const bucketName = currentId || 'prod-logs-backup';
          const mockData = generateS3BlastRadius(currentId, bucketName);
          setInsightData(mockData);
          setError(null);
          setLoading(false);
          return;
        }
        
        // For other types, try API if configured (optional)
        try {
          // Check if API URL is properly configured
          const baseApiUrl = ENV.REACT_APP_URL || ENV.API_URL;
          if (!baseApiUrl || (typeof baseApiUrl === 'string' && baseApiUrl.includes('__STACKGUARD_'))) {
            console.warn('API_URL is not configured, but type is not github/s3. No mock data available.');
            setError('API URL is not configured and no mock data available for this type.');
            setLoading(false);
            return;
          }
          
          // Determine API endpoint
          const apiUrl = `${baseApiUrl}api/findings/${currentId}/insights`;
          
          const response = await axios.get(apiUrl, {
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${localStorage.getItem('jwt')}`,
            },
          });

          setInsightData(response.data?.insights_json_dump || response.data);
          setError(null);
        } catch (apiError) {
          // For other types without mock data, show error
          console.error("Error fetching insights data:", apiError);
          const errorMessage = apiError.response?.data?.message || apiError.message || "Failed to load insights data. Please try again later.";
          setError(errorMessage);
          if (apiError.response?.status !== 404) {
            showToast(errorMessage, "error");
          }
        }
      } catch (error) {
        console.error("Error in fetchInsightsData:", error);
        const errorMessage = "An unexpected error occurred. Please try again later.";
        setError(errorMessage);
        showToast(errorMessage, "error");
      } finally {
        setLoading(false);
      }
    };

    fetchInsightsData();
  }, [currentId, lastFetchedId, showToast, type]);

  // Normalize insight data for use throughout component
  const normalized = useMemo(() => {
    if (!insightData) return {};
    return normalizeInsight(insightData);
  }, [insightData]);

  // Generate node data from API data with nested expansion (universal providers)
  const nodeData = useMemo(
    () => buildNodeData(expandedNodes, normalized, insightData),
    [expandedNodes, normalized, insightData]
  );

  // Dynamic connection labels
  const connectionLabels = useMemo(() => {
    return new Proxy({}, {
      get: (_t, key) => {
        const k = String(key);
        if (k === 'github-token-repositories') return 'HAS_ACCESS_TO';
        if (k === 'repositories-repo-visibility') return 'BY_VISIBILITY';
        if (k === 'repositories-repo-ownership') return 'BY_OWNERSHIP';
        if (k === 'repo-visibility-repo-public') return 'INCLUDES';
        if (k === 'repo-visibility-repo-private') return 'INCLUDES';
        if (k.startsWith('repo-public-repository-public-')) return 'INCLUDES';
        if (k.startsWith('repo-private-repository-private-')) return 'INCLUDES';
        if (k.startsWith('repo-ownership-owner-')) return 'INCLUDES';
        if (k === 'github-token-scopes') return 'HAS_PERMISSIONS';
        if (k === 'scopes-scopes-in-scope') return 'GROUPED_BY';
        if (k === 'scopes-scopes-actions') return 'GROUPED_BY';
        if (k.startsWith('scopes-in-scope-')) return 'INCLUDES';
        if (k.startsWith('scopes-actions-scope-action-')) return 'INCLUDES';
        if (k.includes('scope-action-') && k.includes('-more')) return 'INCLUDES';
        if (k === 'github-token-token-info') return 'CONTAINS';
        if (k.startsWith('scope-repository-')) return 'AFFECTS';
        if (k.startsWith('scopes-scope-')) return 'INCLUDES';
        if (k.includes('-owner')) return 'OWNED_BY';
        if (k.includes('-visibility')) return 'VISIBILITY';
        if (k.includes('-url')) return 'LINKS_TO';
        // HuggingFace scope connections
        if (k === 'github-token-scopes' && k.includes('hf-')) return 'HAS_MODEL_PERMISSIONS';
        if (k === 'scopes-hf-read') return 'BY_READ_ACCESS';
        if (k === 'scopes-hf-write') return 'BY_WRITE_ACCESS';
        if (k === 'scopes-hf-private') return 'BY_PRIVACY';
        if (k.startsWith('hf-read-hf-read-')) return 'INCLUDES';
        if (k.startsWith('hf-write-hf-write-')) return 'INCLUDES';
        if (k.startsWith('hf-private-hf-private-')) return 'INCLUDES';
        if (k.includes('hf-read-true-list') || k.includes('hf-read-false-list')) return 'CONTAINS_MODELS';
        if (k.includes('hf-write-true-list') || k.includes('hf-write-false-list')) return 'CONTAINS_MODELS';
        if (k.includes('hf-private-true-list') || k.includes('hf-private-false-list')) return 'CONTAINS_MODELS';
        // SendGrid functional domain connections
        if (k === 'github-token-scopes' && k.includes('sg-')) return 'HAS_SENDGRID_PERMISSIONS';
        if (k.startsWith('scopes-sg-domain-')) return 'BY_FUNCTIONAL_DOMAIN';
        if (k.startsWith('sg-domain-sg-scope-')) return 'INCLUDES_SCOPES';
        if (k.startsWith('sg-scope-sg-subscope-')) return 'INCLUDES_SUB_SCOPES';
        if (k.startsWith('sg-subscope-sg-permissions-')) return 'CONTAINS_PERMISSIONS';
        if (k.startsWith('sg-scope-sg-permissions-')) return 'CONTAINS_PERMISSIONS';
        if (k.includes('sg-2fa')) return 'SECURITY_FEATURE';
        // Postman connections
        if (k === 'github-token-workspaces') return 'HAS_ACCESS_TO';
        if (k === 'github-token-roles') return 'HAS_ROLES';
        if (k === 'github-token-token-info') return 'CONTAINS';
        if (k === 'workspaces-workspace-type') return 'GROUPED_BY_TYPE';
        if (k === 'workspaces-workspace-visibility') return 'GROUPED_BY_VISIBILITY';
        if (k.startsWith('workspace-type-workspace-type-')) return 'INCLUDES';
        if (k.startsWith('workspace-visibility-workspace-visibility-')) return 'INCLUDES';
        if (k.startsWith('workspace-type-') && k.includes('-list')) return 'CONTAINS_WORKSPACES';
        if (k.startsWith('workspace-visibility-') && k.includes('-list')) return 'CONTAINS_WORKSPACES';
        if (k === 'roles-role-') return 'INCLUDES_ROLES';
        if (k.startsWith('role-') && k.includes('-list')) return 'CONTAINS_ROLES';
        if (k.includes('workspace-type-')) return 'BY_TYPE';
        if (k.includes('workspace-visibility-')) return 'BY_VISIBILITY';
        if (k.includes('role-')) return 'BY_ROLE';
        // S3/AWS connections
        if (k === 'github-token-buckets') return 'AFFECTS';
        if (k === 'buckets-buckets-list') return 'CONTAINS';
        if (k === 'github-token-iam-permissions') return 'HAS_PERMISSIONS';
        if (k === 'iam-permissions-iam-permissions-list') return 'CONTAINS';
        return '';
      }
    });
  }, []);

  const handleNodeClick = useCallback((nodeId) => {
    if (expandedNodes.has(nodeId)) {
      const newVisible = new Set(visibleNodes);
      const newExpanded = new Set(expandedNodes);

      const toRemove = new Set();

      const collectDescendants = (currentId) => {
        const current = nodeData[currentId];
        if (!current) return;
        current.connections.forEach((childId) => {
          if (!newVisible.has(childId)) return; // already hidden
          // If any other visible node (not already scheduled for removal) connects to this child, keep it
          const hasExternalParent = Array.from(newVisible).some((vid) => {
            if (vid === currentId) return false;
            if (toRemove.has(vid)) return false;
            const vNode = nodeData[vid];
            return vNode && vNode.connections.includes(childId);
          });
          if (!hasExternalParent) {
            toRemove.add(childId);
            collectDescendants(childId);
          }
        });
      };

      collectDescendants(nodeId);

      // Apply removals
      toRemove.forEach((id) => {
        newVisible.delete(id);
        newExpanded.delete(id);
      });

      newExpanded.delete(nodeId);
      setVisibleNodes(newVisible);
      setExpandedNodes(newExpanded);
    } else {
      // Expand: show connected nodes
      const newVisible = new Set(visibleNodes);
      const newExpanded = new Set(expandedNodes);
      
      const currentNode = nodeData[nodeId];
      if (currentNode && currentNode.connections) {
        currentNode.connections.forEach(connectedId => {
          newVisible.add(connectedId);
        });
      }
      
      newExpanded.add(nodeId);
      setVisibleNodes(newVisible);
      setExpandedNodes(newExpanded);
    }
  }, [visibleNodes, expandedNodes, nodeData]);

  // Convert to React Flow nodes
  const nodes = useMemo(() => {
    return Array.from(visibleNodes).map(nodeId => {
      const node = nodeData[nodeId];
      if (!node) return null;
      
      const isExpanded = expandedNodes.has(nodeId);
      const hasConnections = node.connections.length > 0;
      
      // Determine node size based on type
      const isMainNode = ['github-token', 'repositories', 'scopes', 'token-info', 'buckets', 'iam-permissions'].includes(nodeId);
      const isOverflowNode = nodeId.endsWith('-more') || nodeId === 'repositories-more';
      const isListNode = nodeId.endsWith('-list');
      const explicitWidth = node.width;
      const explicitHeight = node.height;
      const nodeWidth = explicitWidth || (isMainNode ? 160 : isOverflowNode ? 110 : 140);
      const nodeHeight = explicitHeight || (isMainNode ? 72 : isOverflowNode ? 44 : 56);
      
      return {
        id: nodeId,
        data: { 
          label: (
            <div className="relative text-center w-full h-full p-2 flex items-center justify-center" style={{overflow: 'visible'}}>
              {isListNode ? (
                <div className="text-[14px] text-gray-800 select-none leading-tight w-full h-full overflow-y-auto" style={{wordBreak:'break-word'}}>
                  <div className="font-semibold mb-2 text-center">{node.label}</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                    {(node.items || []).map((it, idx) => (
                      <div key={idx} className="flex items-center justify-start gap-2">
                        <span>• {it.name}</span>
                        {it.url && (
                          <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>🔗</a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`font-medium text-gray-800 select-none w-full h-full flex flex-col items-center justify-center px-2 py-1 ${isMainNode ? 'text-[11px]' : 'text-[10px]'}`}>
                  {node.label.split('\n').map((line, index) => (
                    <div key={index} className="text-center">{line}</div>
                  ))}
                </div>
              )}
              {/* Expansion indicator for nodes with connections */}
              {hasConnections && !isOverflowNode && (
                <div 
                  className="absolute -top-6 -right-6 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white transition-all duration-300 shadow-lg z-10"
                  style={{ 
                    backgroundColor: isExpanded ? "#ef4444" : "#10b981",
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNodeClick(nodeId);
                  }}
                >
                  {isExpanded ? "−" : "+"}
                </div>
              )}
            </div>
          )
        },
        position: { x: node.x, y: node.y },
        style: {
          background: '#ffffff',
          border: isExpanded ? "3px solid #441a9a" : "2px solid #441a9a",
          borderRadius: '8px',
          width: nodeWidth,
          height: nodeHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: hasConnections ? 'visible' : 'hidden',
          filter: hasConnections ? 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))' : 'none',
          cursor: hasConnections ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
        },
        type: 'default',
      };
    }).filter(Boolean);
  }, [visibleNodes, expandedNodes, handleNodeClick, nodeData]);

  // Convert to React Flow edges
  const edges = useMemo(() => {
    const edgeList = [];
    const processedEdges = new Set(); // Track processed edges to avoid duplicates
    
    visibleNodes.forEach(nodeId => {
      const node = nodeData[nodeId];
      if (!node || !node.connections) return;
      
      node.connections.forEach(connectedId => {
        // Only create edge if both nodes are visible and edge hasn't been processed
        if (visibleNodes.has(connectedId)) {
          const edgeId = `${nodeId}-${connectedId}`;
          const reverseEdgeId = `${connectedId}-${nodeId}`;
          
          // Avoid duplicate edges
          if (!processedEdges.has(edgeId) && !processedEdges.has(reverseEdgeId)) {
            processedEdges.add(edgeId);
            
            const key1 = getConnectionKey(nodeId, connectedId);
            const key2 = getConnectionKey(connectedId, nodeId);
            // Reduce label noise: only show labels for top-level connections
            const isTopLevel = nodeId === 'github-token' || connectedId === 'github-token' || nodeId === 'repositories' || nodeId === 'scopes';
            const label = isTopLevel ? (connectionLabels[key1] || connectionLabels[key2] || '') : '';
            
            edgeList.push({
              id: edgeId,
              source: nodeId,
              target: connectedId,
              label: label,
              type: 'straight',
              style: {
                stroke: '#64748b',
                strokeWidth: 2,
              },
              labelStyle: {
                fontSize: 10,
                fill: '#6b7280',
                fontWeight: 500,
              },
              labelShowBg: true,
              labelBgStyle: {
                fill: '#ffffff',
                fillOpacity: 1,
                stroke: 'rgba(0,0,0,0.8)',
                strokeWidth: 1,
              },
              labelBgPadding: [2, 4],
              labelBgBorderRadius: 3,
            });
          }
        }
      });
    });
    
    return edgeList;
  }, [visibleNodes, connectionLabels, nodeData]);

  const layouted = useMemo(() => {
    const g = new dagre.graphlib.Graph();
    g.setGraph({ rankdir: 'LR', nodesep: 200, ranksep: 180, edgesep: 160 });
    g.setDefaultEdgeLabel(() => ({}));

    // Prepare nodes with approximate sizes
    const rfNodes = Array.from(visibleNodes).map((id) => {
      const isMain = ['github-token', 'repositories', 'scopes', 'token-info', 'buckets', 'iam-permissions'].includes(id);
      const isOverflow = id.endsWith('-more') || id === 'repositories-more';
      const originalNode = nodeData[id];
      const width = (originalNode && originalNode.width) ? originalNode.width : (isMain ? 160 : isOverflow ? 110 : 140);
      const height = (originalNode && originalNode.height) ? originalNode.height : (isMain ? 72 : isOverflow ? 44 : 56);
      g.setNode(id, { width, height });
      return id;
    });

    // Edges - only add edges between visible nodes
    rfNodes.forEach((id) => {
      const node = nodeData[id];
      if (!node || !node.connections) return;
      node.connections.forEach((targetId) => {
        if (visibleNodes.has(targetId) && visibleNodes.has(id)) {
          g.setEdge(id, targetId);
        }
      });
    });

    dagre.layout(g);

    const laidOutNodes = Array.from(visibleNodes).map((id) => {
      const original = nodeData[id];
      if (!original) return null;
      const dagrePos = g.node(id);
      return {
        ...original,
        x: dagrePos?.x ?? original.x,
        y: dagrePos?.y ?? original.y,
      };
    }).reduce((acc, n) => { if (n) acc[n.id] = n; return acc; }, {});

    return {
      nodes: laidOutNodes,
      edges: edges,
    };
  }, [visibleNodes, nodeData, edges]);

  const [reactFlowNodes, setNodes, onNodesChange] = useNodesState(nodes);
  const [reactFlowEdges, setEdges, onEdgesChange] = useEdgesState(edges);

  // Update React Flow nodes when our state changes
  React.useEffect(() => {
    // Use dagre positions when available
    const n = Array.from(visibleNodes).map((id) => {
      const original = nodeData[id];
      if (!original) return null;
      const laid = layouted.nodes[id] || original;
      const isMainNode = ['github-token', 'repositories', 'scopes', 'token-info', 'buckets', 'iam-permissions'].includes(id);
      const isOverflowNode = id.endsWith('-more') || id === 'repositories-more';
      const isListNode = id.endsWith('-list');
      const explicitWidth = original.width;
      const explicitHeight = original.height;
      const nodeWidth = explicitWidth || (isMainNode ? 160 : isOverflowNode ? 110 : 140);
      const nodeHeight = explicitHeight || (isMainNode ? 72 : isOverflowNode ? 44 : 56);
      const hasConnections = (original.connections || []).length > 0;
      return {
        id,
        data: {
          label: (
            <div className="relative text-center w-full h-full p-2 flex items-center justify-center" style={{overflow: 'visible'}}>
              {isListNode ? (
                <div className="text-[14px] text-gray-800 select-none leading-tight w-full h-full overflow-y-auto" style={{wordBreak:'break-word'}}>
                  <div className="font-semibold mb-2 text-center">{original.label}</div>
                  <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                    {(original.items || []).map((it, idx) => (
                      <div key={idx} className="flex items-center justify-start gap-2">
                        <span>• {it.name}</span>
                        {it.url && (
                          <a href={it.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline" onClick={(e) => e.stopPropagation()}>🔗</a>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className={`font-medium text-gray-800 select-none w-full h-full flex flex-col items-center justify-center px-2 py-1 ${isMainNode ? 'text-[11px]' : 'text-[10px]'}`}>
                  {original.label.split('\n').map((line, index) => (
                    <div key={index} className="text-center">{line}</div>
                  ))}
                </div>
              )}
              {hasConnections && !isOverflowNode && (
                <div 
                  className="absolute -top-6 -right-6 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white transition-all duration-300 shadow-lg z-10"
                  style={{ 
                    backgroundColor: expandedNodes.has(id) ? "#ef4444" : "#10b981",
                    cursor: 'pointer'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNodeClick(id);
                  }}
                >
                  {expandedNodes.has(id) ? "−" : "+"}
                </div>
              )}
            </div>
          )
        },
        position: { x: laid.x, y: laid.y },
        style: {
          background: '#ffffff',
          border: expandedNodes.has(id) ? "3px solid #441a9a" : "2px solid #441a9a",
          borderRadius: '8px',
          width: nodeWidth,
          height: nodeHeight,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: hasConnections ? 'visible' : 'hidden',
          filter: (original.connections.length > 0) ? 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.1))' : 'none',
          cursor: (original.connections.length > 0) ? 'pointer' : 'default',
          transition: 'all 0.3s ease',
        },
        type: 'default',
      };
    }).filter(Boolean);
    setNodes(n);
  }, [layouted, visibleNodes, nodeData, expandedNodes, setNodes, handleNodeClick]);

  React.useEffect(() => {
    setEdges(edges);
  }, [edges, setEdges]);

    const onNodeClick = useCallback((event, node) => {
    const nodeId = node.id;
    const hasConnections = nodeData[nodeId]?.connections.length > 0;
    if (hasConnections) {
      handleNodeClick(nodeId);
    }
  }, [handleNodeClick, nodeData]);

  // Loading state
  if (loading) {
    return <ShimmerLoader height="400px" lines={5} />;
  }

  // Error state
  if (error) {
    return (
      <div className="w-full h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Error Loading Insights</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // No data state
  if (!insightData) {
    return (
      <div className="w-full h-full bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">No Insights Data</h3>
          <p className="text-gray-600">No insights data available for this finding.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-gray-50 relative flex flex-col">
      {/* Main Graph Area */}
      <div className="flex-1 relative">

        <ReactFlow
          nodes={reactFlowNodes}
          edges={reactFlowEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onNodeClick={onNodeClick}
          onPaneClick={() => setCollapsePanels((v) => !v)}
          fitView
          fitViewOptions={{ padding: 0.1 }}
          minZoom={0.1}
          maxZoom={2}
          proOptions={{ hideAttribution: true }}
        >
          <Controls showInteractive={false} />
          <Background />
        </ReactFlow>
      </div>

      {/* Bottom Panels */}
      <BottomPanels normalized={normalized} collapsePanels={collapsePanels} />
    </div>
  );
};

export default ContextualNodeGraph;